import React, {
  useRef,
  useEffect,
  useState,
  memo,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { SimliClient, generateSimliSessionToken, LogLevel } from "simli-client";
import { apiFetch } from "@/lib/jwt-auth-client";
import { cn } from "@/lib/utils";
import { cpLog } from "@/features/companion/lib/companionLogger";
import type { AvatarHandle, SpeechResult } from "./types";

interface SimliAvatarProps {
  faceId: string;
  voiceId: string;
  /** Called synchronously when the user taps to start (before LiveKit connects). */
  onConnectionAttempt?: () => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onStartSpeaking?: () => void;
  onStopSpeaking?: () => void;
  /** Fired after TTS audio for a text chunk has been fully sent to the avatar.
   *  `durationMs` is derived from the PCM byte count and sample rate. */
  onTextAudioReady?: (text: string, durationMs: number) => void;
  isMuted?: boolean;
  volume?: number;
  /** Skip rendering the "tap to start" gate button (for decorative/read-only embeds
   *  where a parent is already a <button> — avoids nested-button hydration errors). */
  hideGestureGate?: boolean;
  /** Static image shown over the (black) video until the live stream starts, then cross-faded
   *  out. Lets a parent avoid showing a black circle while Simli connects. Purely decorative —
   *  it does not affect connection, gesture, or speech behavior. */
  posterSrc?: string;
}

interface SpeechQueueItem {
  text: string;
  waitId?: number;
}

interface PendingSpeechWait {
  id: number;
  text: string;
  started: boolean;
  streamed: boolean;
  settled: boolean;
  timeoutId: number | null;
  removeAbortListener?: () => void;
  promise: Promise<SpeechResult>;
  resolve: (result: SpeechResult) => void;
}

const DEFAULT_SPEECH_WAIT_TIMEOUT_MS = 15_000;

function resolveSimliLogLevel(value: string | undefined, debugEnabled: boolean): LogLevel {
  const raw = (value ?? "").trim().toLowerCase();
  if (raw === "debug") return LogLevel.DEBUG;
  if (raw === "info") return LogLevel.INFO;
  if (raw === "critical") return LogLevel.CRITICAL;
  if (raw === "error") return LogLevel.ERROR;
  return debugEnabled ? LogLevel.DEBUG : LogLevel.ERROR;
}

/**
 * Persistent AudioContext kept alive across the page so the browser retains
 * "sticky activation" for Web Audio.  LiveKit creates its own AudioContext
 * when the room connects — if no running context exists on the page at that
 * point, Chrome blocks it with "AudioContext was not allowed to start".
 */
let _pageAudioCtx: AudioContext | null = null;
/** Track whether the user has provided a gesture at least once in this page session. */
let _userGestureProvided = false;

/** Returns true if Web Audio is already unlocked from a prior gesture. */
function isWebAudioUnlocked(): boolean {
  return _userGestureProvided && !!_pageAudioCtx && _pageAudioCtx.state === "running";
}

/** Unlock Web Audio in the same synchronous turn as a user gesture (Chrome autoplay policy). */
function unlockWebAudioForGesture(audioEl: HTMLAudioElement | null) {
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!_pageAudioCtx || _pageAudioCtx.state === "closed") {
      _pageAudioCtx = new AC();
    }
    if (_pageAudioCtx.state === "suspended") {
      void _pageAudioCtx.resume();
    }
    const buf = _pageAudioCtx.createBuffer(1, 1, 22050);
    const src = _pageAudioCtx.createBufferSource();
    src.buffer = buf;
    src.connect(_pageAudioCtx.destination);
    src.start(0);
  } catch {
    /* ignore */
  }
  if (audioEl) {
    void audioEl.play().catch(() => {});
  }
}

export const SimliAvatar = memo(
  forwardRef<AvatarHandle, SimliAvatarProps>(function SimliAvatar(
    {
      faceId,
      voiceId,
      onConnectionAttempt,
      onConnected,
      onDisconnected,
      onStartSpeaking,
      onStopSpeaking,
      onTextAudioReady,
      isMuted = false,
      volume = 1,
      hideGestureGate = false,
      posterSrc,
    },
    ref,
  ) {
    const simliDebugLogs = import.meta.env.VITE_SIMLI_DEBUG_LOGS === "1";
    const simliSdkLogLevel = resolveSimliLogLevel(
      import.meta.env.VITE_SIMLI_SDK_LOG_LEVEL,
      simliDebugLogs,
    );

    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const clientRef = useRef<SimliClient | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** LiveKit/Simli must not connect until a user gesture (browser autoplay policy). */
    const [awaitingUserGesture, setAwaitingUserGesture] = useState(true);

    // Stable refs so connection effect doesn't re-run when callbacks change identity
    const onConnectedRef = useRef(onConnected);
    const onDisconnectedRef = useRef(onDisconnected);
    const onConnectionAttemptRef = useRef(onConnectionAttempt);
    const onStartSpeakingRef = useRef(onStartSpeaking);
    const onStopSpeakingRef = useRef(onStopSpeaking);
    useEffect(() => {
      onConnectedRef.current = onConnected;
    });
    useEffect(() => {
      onDisconnectedRef.current = onDisconnected;
    });
    useEffect(() => {
      onConnectionAttemptRef.current = onConnectionAttempt;
    });
    useEffect(() => {
      onStartSpeakingRef.current = onStartSpeaking;
    });
    useEffect(() => {
      onStopSpeakingRef.current = onStopSpeaking;
    });
    const onTextAudioReadyRef = useRef(onTextAudioReady);
    useEffect(() => {
      onTextAudioReadyRef.current = onTextAudioReady;
    });

    // Guard: only call onStopSpeaking after Simli has actually started speaking.
    const isSpeakingRef = useRef(false);

    // Stable ref for voiceId so the queue processor always uses the latest value
    const voiceIdRef = useRef(voiceId);
    useEffect(() => {
      voiceIdRef.current = voiceId;
    });

    // Stable ref for isReady so queue processor can check without stale closure
    const isReadyRef = useRef(false);
    useEffect(() => {
      isReadyRef.current = isReady;
    });

    // ── PCM push buffer (pipecat runtime path) ──────────────────────────────
    // Accumulate small PCM chunks to >=MIN_PCM_PUSH bytes before calling
    // sendAudioData. This matches the manual mode's 6000-byte minimum and
    // prevents overwhelming Simli's WebRTC pipeline with hundreds of tiny
    // sendAudioData calls when the adapter delivers audio in a burst.
    const MIN_PCM_PUSH = 6000;
    const pcmPushPendingRef = useRef(new Uint8Array(0));

    // ── Diagnostics: track sendAudioData calls ────────────────────────────
    const sendCountRef = useRef(0);
    const sendBytesRef = useRef(0);
    const lastSendAtRef = useRef(0);
    const speakingSilentLogRef = useRef(0);

    // ── TTS Queue ──────────────────────────────────────────────────────────────
    // Sentences are enqueued via speak() and processed sequentially.
    // sendAudioData appends to Simli's internal buffer, so sequential sends
    // produce continuous lip-synced speech without gaps.
    const queueRef = useRef<SpeechQueueItem[]>([]);
    const drainingRef = useRef(false);
    const ttsAbortRef = useRef<AbortController | null>(null);
    const ttsEpochRef = useRef(0);
    const speechWaitSeqRef = useRef(0);
    const speechWaitsRef = useRef<Map<number, PendingSpeechWait>>(new Map());
    const activeSpeechWaitIdRef = useRef<number | null>(null);

    const settleSpeechWait = useCallback((id: number, result: SpeechResult) => {
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled) return;
      wait.settled = true;
      if (wait.timeoutId !== null) {
        window.clearTimeout(wait.timeoutId);
        wait.timeoutId = null;
      }
      wait.removeAbortListener?.();
      speechWaitsRef.current.delete(id);
      if (activeSpeechWaitIdRef.current === id) {
        activeSpeechWaitIdRef.current = null;
      }
      wait.resolve(result);
    }, []);

    const settleAllSpeechWaits = useCallback(
      (status: SpeechResult["status"], reason: string) => {
        for (const wait of Array.from(speechWaitsRef.current.values())) {
          settleSpeechWait(wait.id, { status, text: wait.text, reason });
        }
      },
      [settleSpeechWait],
    );

    const markActiveSpeechWaitStarted = useCallback(() => {
      const id = activeSpeechWaitIdRef.current;
      if (id === null) return;
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled) return;
      wait.started = true;
    }, []);

    const markSpeechWaitStreamed = useCallback(
      (id: number) => {
        const wait = speechWaitsRef.current.get(id);
        if (!wait || wait.settled) return;
        wait.streamed = true;
        if (wait.started && !isSpeakingRef.current) {
          settleSpeechWait(id, { status: "completed", text: wait.text });
        }
      },
      [settleSpeechWait],
    );

    const completeActiveSpeechWaitIfReady = useCallback(() => {
      const id = activeSpeechWaitIdRef.current;
      if (id === null) return;
      const wait = speechWaitsRef.current.get(id);
      if (!wait || wait.settled || !wait.started || !wait.streamed) return;
      settleSpeechWait(id, { status: "completed", text: wait.text });
    }, [settleSpeechWait]);

    const drainQueue = useCallback(async () => {
      if (drainingRef.current || !isReadyRef.current || !clientRef.current) return;
      if (queueRef.current.length === 0) return;

      drainingRef.current = true;

      try {
        while (queueRef.current.length > 0 && clientRef.current) {
          const item = queueRef.current.shift()!;
          const { text } = item;
          if (item.waitId !== undefined) {
            activeSpeechWaitIdRef.current = item.waitId;
          }
          let abortController: AbortController | null = null;
          try {
            abortController = new AbortController();
            ttsAbortRef.current = abortController;
            const ttsEpoch = ttsEpochRef.current;
            const isCurrentTts = () =>
              ttsEpochRef.current === ttsEpoch && !abortController?.signal.aborted;
            cpLog("simli-tts", `→ "${text.substring(0, 60)}${text.length > 60 ? "…" : ""}"`);
            const response = await apiFetch("/core/tts", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text, voiceId: voiceIdRef.current }),
              signal: abortController.signal,
            });
            if (!isCurrentTts()) {
              await response.body?.cancel().catch(() => undefined);
              continue;
            }

            if (!response.ok) {
              const errText = await response.text().catch(() => "");
              cpLog("simli-tts-error", `TTS ${response.status}: ${errText}`);
              if (item.waitId !== undefined) {
                settleSpeechWait(item.waitId, {
                  status: "error",
                  text,
                  reason: `TTS ${response.status}`,
                });
              }
              continue;
            }
            if (!clientRef.current || !response.body) {
              if (item.waitId !== undefined) {
                settleSpeechWait(item.waitId, {
                  status: "error",
                  text,
                  reason: "Avatar disconnected before TTS stream was available",
                });
              }
              break;
            }

            // Stream PCM chunks as they arrive from the configured TTS provider (via /core/tts).
            // Accumulate to >=6000 bytes before sending (Simli's preferred chunk size).
            // Ensure even byte count for PCM16 sample alignment.
            const reader = response.body.getReader();
            let totalBytes = 0;
            let pendingBuffer = new Uint8Array(0);
            const MIN_CHUNK = 6000; // Simli recommended chunk size

            while (true) {
              const { done, value } = await reader.read();
              if (!isCurrentTts()) {
                await reader.cancel().catch(() => undefined);
                break;
              }

              if (done || !clientRef.current) {
                // Flush remaining bytes (ensure even length for PCM16 alignment)
                if (pendingBuffer.length > 0 && clientRef.current && isCurrentTts()) {
                  const evenLen = pendingBuffer.length & ~1; // round down to even
                  if (evenLen > 0) {
                    clientRef.current.sendAudioData(pendingBuffer.slice(0, evenLen));
                    totalBytes += evenLen;
                  }
                }
                break;
              }

              // Append new data to pending buffer
              const merged = new Uint8Array(pendingBuffer.length + value.byteLength);
              merged.set(pendingBuffer);
              merged.set(value, pendingBuffer.length);
              pendingBuffer = merged;

              // Send chunks of MIN_CHUNK size (even-aligned)
              while (pendingBuffer.length >= MIN_CHUNK && clientRef.current && isCurrentTts()) {
                const evenLen = MIN_CHUNK & ~1; // ensure even
                clientRef.current.sendAudioData(pendingBuffer.slice(0, evenLen));
                pendingBuffer = pendingBuffer.slice(evenLen);
                totalBytes += evenLen;
              }
            }

            if (!isCurrentTts()) continue;
            // Report chunk timing — 16-bit PCM at sampleRate
            const sampleRate = 16000; // matches /core/tts default
            const durationMs = totalBytes > 0 ? (totalBytes / (sampleRate * 2)) * 1000 : 0;
            if (durationMs > 0) onTextAudioReadyRef.current?.(text, durationMs);
            cpLog("simli-streamed", `${(totalBytes / 32000).toFixed(1)}s of audio`);
            if (item.waitId !== undefined) {
              markSpeechWaitStreamed(item.waitId);
              const wait = speechWaitsRef.current.get(item.waitId);
              if (wait) {
                await wait.promise;
              }
            }
          } catch (err) {
            const errName =
              err && typeof err === "object" && "name" in err
                ? String((err as { name?: unknown }).name)
                : "";
            const isAbort = errName === "AbortError";
            if (!isAbort) {
              cpLog(
                "simli-tts-error",
                `TTS error: ${err instanceof Error ? err.message : String(err)}`,
              );
            }
            if (!isAbort && item.waitId !== undefined) {
              settleSpeechWait(item.waitId, {
                status: "error",
                text,
                reason: err instanceof Error ? err.message : String(err),
              });
            }
          } finally {
            if (ttsAbortRef.current === abortController) {
              ttsAbortRef.current = null;
            }
          }
        }
      } finally {
        drainingRef.current = false;
      }
    }, [markSpeechWaitStreamed, settleSpeechWait]);

    const mountedRef = useRef(true);
    const activationLockRef = useRef(false);
    const CONNECTION_TIMEOUT_MS = 30_000;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2_000;
    const retryCountRef = useRef(0);
    const wasEverReadyRef = useRef(false);
    const faceIdSeqRef = useRef(0);
    const connectAttemptRef = useRef(0);
    const connectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mediaListenerCleanupRef = useRef<(() => void) | null>(null);

    const clearConnectTimers = useCallback(() => {
      if (connectionTimerRef.current) {
        clearTimeout(connectionTimerRef.current);
        connectionTimerRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    }, []);

    useEffect(() => {
      const apiKey = import.meta.env.VITE_SIMLI_API_KEY ?? "";
      if (!apiKey) {
        cpLog("simli-error", "Missing VITE_SIMLI_API_KEY");
        setError("Avatar API key not configured");
        onDisconnectedRef.current?.();
      }
    }, []);

    const connectSimli = useCallback(async () => {
      const apiKey = import.meta.env.VITE_SIMLI_API_KEY ?? "";
      if (!apiKey) return;

      const videoEl = videoRef.current;
      const audioEl = audioRef.current;
      if (!videoEl || !audioEl) return;

      const seq = faceIdSeqRef.current;
      const attemptId = ++connectAttemptRef.current;

      retryCountRef.current = 0;
      clearConnectTimers();
      mediaListenerCleanupRef.current?.();
      mediaListenerCleanupRef.current = null;
      isSpeakingRef.current = false;

      const stillValid = () =>
        mountedRef.current &&
        faceIdSeqRef.current === seq &&
        connectAttemptRef.current === attemptId;

      const removeMediaListeners = () => {
        mediaListenerCleanupRef.current?.();
        mediaListenerCleanupRef.current = null;
      };

      const handleConnectionFailure = (reason: string) => {
        if (!stillValid()) return;

        if (connectionTimerRef.current) {
          clearTimeout(connectionTimerRef.current);
          connectionTimerRef.current = null;
        }

        isReadyRef.current = false;
        setIsReady(false);
        isSpeakingRef.current = false;
        removeMediaListeners();

        if (clientRef.current) {
          if (wasEverReadyRef.current) {
            clientRef.current.stop().catch(() => {});
          }
          clientRef.current = null;
          wasEverReadyRef.current = false;
        }

        if (retryCountRef.current < MAX_RETRIES && stillValid()) {
          retryCountRef.current += 1;
          const delay = RETRY_DELAY_MS * Math.pow(2, retryCountRef.current - 1);
          cpLog(
            "simli-retry",
            `Retry ${retryCountRef.current}/${MAX_RETRIES} in ${delay}ms (reason: ${reason})`,
          );
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            if (!stillValid()) return;
            void runConnectAttempt();
          }, delay);
        } else {
          cpLog(
            "simli-error",
            `Giving up after ${retryCountRef.current} retries (reason: ${reason})`,
          );
          if (stillValid()) {
            setError("Avatar connection failed");
            activationLockRef.current = false;
            setAwaitingUserGesture(true);
            onDisconnectedRef.current?.();
          }
        }
      };

      const runConnectAttempt = async () => {
        if (!stillValid()) return;

        removeMediaListeners();

        if (clientRef.current) {
          if (wasEverReadyRef.current) {
            clientRef.current.stop().catch(() => {});
          }
          clientRef.current = null;
          wasEverReadyRef.current = false;
        }

        try {
          cpLog(
            "simli-connect",
            `connecting... (attempt ${retryCountRef.current + 1}/${MAX_RETRIES + 1})`,
          );

          const { session_token } = await generateSimliSessionToken({
            apiKey,
            config: {
              faceId,
              handleSilence: true,
              maxSessionLength: 3600,
              maxIdleTime: 600,
            },
          });
          if (!stillValid()) return;

          cpLog("simli-init", "session token obtained");

          const client = new SimliClient(
            session_token,
            videoEl,
            audioEl,
            null,
            simliSdkLogLevel,
            "livekit",
          );
          clientRef.current = client;

          connectionTimerRef.current = setTimeout(() => {
            connectionTimerRef.current = null;
            if (!stillValid() || isReadyRef.current) return;
            cpLog("simli-timeout", `No 'start' event after ${CONNECTION_TIMEOUT_MS}ms`);
            handleConnectionFailure("Connection timed out");
          }, CONNECTION_TIMEOUT_MS);

          client.on("start", () => {
            if (!stillValid()) return;
            if (connectionTimerRef.current) {
              clearTimeout(connectionTimerRef.current);
              connectionTimerRef.current = null;
            }
            retryCountRef.current = 0;
            wasEverReadyRef.current = true;
            cpLog("simli-start", "avatar is live");
            isReadyRef.current = true;
            setIsReady(true);
            onConnectedRef.current?.();
            drainQueue();

            const retryPlay = () => {
              if (audioEl.paused && (audioEl.srcObject || audioEl.src)) {
                void audioEl.play().catch(() => {});
              }
            };
            retryPlay();
            setTimeout(retryPlay, 500);
            setTimeout(retryPlay, 1500);
          });

          client.on("stop", () => {
            if (!stillValid()) return;
            cpLog("simli-stop", "stream stopped");
            const wasReady = isReadyRef.current;
            isReadyRef.current = false;
            setIsReady(false);
            isSpeakingRef.current = false;
            settleAllSpeechWaits("error", "Simli stream stopped");
            if (wasReady) {
              handleConnectionFailure("Stream stopped unexpectedly");
            }
          });

          client.on("speaking", () => {
            if (!stillValid()) return;
            speakingSilentLogRef.current++;
            cpLog(
              "simli-speaking",
              `#${speakingSilentLogRef.current} sends=${sendCountRef.current} bytes=${sendBytesRef.current}`,
            );
            isSpeakingRef.current = true;
            markActiveSpeechWaitStarted();
            onStartSpeakingRef.current?.();
          });

          client.on("silent", () => {
            if (!stillValid()) return;
            if (!isSpeakingRef.current) return;
            speakingSilentLogRef.current++;
            cpLog(
              "simli-silent",
              `#${speakingSilentLogRef.current} sends=${sendCountRef.current} bytes=${sendBytesRef.current}`,
            );
            isSpeakingRef.current = false;
            onStopSpeakingRef.current?.();
            completeActiveSpeechWaitIfReady();
          });

          client.on("error", (detail) => {
            cpLog("simli-error", `Error: ${detail}`);
          });

          client.on("startup_error", (detail) => {
            cpLog("simli-error", `Startup error: ${detail}`);
            if (stillValid()) handleConnectionFailure(`Startup error: ${detail}`);
          });

          const onStalled = () => cpLog("simli-video", "stalled");
          const onWaiting = () => cpLog("simli-video", "waiting");
          const onPlaying = () => cpLog("simli-video", "playing");
          videoEl.addEventListener("stalled", onStalled);
          videoEl.addEventListener("waiting", onWaiting);
          videoEl.addEventListener("playing", onPlaying);
          const onAudioStalled = () => cpLog("simli-audio", "stalled");
          const onAudioWaiting = () => cpLog("simli-audio", "waiting");
          audioEl.addEventListener("stalled", onAudioStalled);
          audioEl.addEventListener("waiting", onAudioWaiting);
          mediaListenerCleanupRef.current = () => {
            videoEl.removeEventListener("stalled", onStalled);
            videoEl.removeEventListener("waiting", onWaiting);
            videoEl.removeEventListener("playing", onPlaying);
            audioEl.removeEventListener("stalled", onAudioStalled);
            audioEl.removeEventListener("waiting", onAudioWaiting);
          };

          await client.start();
        } catch (err) {
          cpLog(
            "simli-error",
            `Connection failed: ${err instanceof Error ? err.message : String(err)}`,
          );
          if (stillValid()) {
            handleConnectionFailure(err instanceof Error ? err.message : String(err));
          }
        }
      };

      await runConnectAttempt();
    }, [
      faceId,
      drainQueue,
      simliSdkLogLevel,
      clearConnectTimers,
      completeActiveSpeechWaitIfReady,
      markActiveSpeechWaitStarted,
      settleAllSpeechWaits,
    ]);

    // Tear down session when face changes or unmounting.
    // If Web Audio was already unlocked by a prior gesture, skip the gate and auto-connect.
    useEffect(() => {
      mountedRef.current = true;
      activationLockRef.current = false;
      isSpeakingRef.current = false;
      retryCountRef.current = 0;
      clearConnectTimers();
      mediaListenerCleanupRef.current?.();
      mediaListenerCleanupRef.current = null;

      if (isWebAudioUnlocked()) {
        // User already tapped once this session — skip the gate and auto-connect
        setAwaitingUserGesture(false);
        activationLockRef.current = true;
        onConnectionAttemptRef.current?.();
        void connectSimli();
      } else {
        setAwaitingUserGesture(true);
      }

      return () => {
        faceIdSeqRef.current += 1;
        connectAttemptRef.current += 1;
        mountedRef.current = false;
        clearConnectTimers();
        mediaListenerCleanupRef.current?.();
        mediaListenerCleanupRef.current = null;
        isSpeakingRef.current = false;
        queueRef.current = [];
        drainingRef.current = false;
        settleAllSpeechWaits("cancelled", "Simli avatar unmounted");
        if (wasEverReadyRef.current && clientRef.current) {
          clientRef.current.stop().catch(() => {});
        }
        clientRef.current = null;
        wasEverReadyRef.current = false;
        isReadyRef.current = false;
        setIsReady(false);
      };
    }, [faceId, clearConnectTimers, connectSimli, settleAllSpeechWaits]);

    const startFromUserGesture = useCallback(() => {
      if (activationLockRef.current) return;
      activationLockRef.current = true;
      setAwaitingUserGesture(false);
      _userGestureProvided = true;
      unlockWebAudioForGesture(audioRef.current);
      onConnectionAttemptRef.current?.();
      void connectSimli();
    }, [connectSimli]);

    const handleUserGestureStart = useCallback(
      (e: React.PointerEvent) => {
        e.stopPropagation();
        startFromUserGesture();
      },
      [startFromUserGesture],
    );

    // Expose imperative API to parent
    useImperativeHandle(
      ref,
      () => ({
        start: startFromUserGesture,
        speak(text: string) {
          const trimmed = text.trim();
          if (!trimmed) return;
          queueRef.current.push({ text: trimmed });
          drainQueue();
        },
        speakAndWait(text, options = {}) {
          const trimmed = text.trim();
          if (!trimmed) {
            return Promise.resolve({ status: "cancelled", text: trimmed, reason: "empty text" });
          }
          if (options.signal?.aborted) {
            return Promise.resolve({
              status: "cancelled",
              text: trimmed,
              reason: "caller aborted before speech was queued",
            });
          }

          const id = ++speechWaitSeqRef.current;
          const timeoutMs = Math.max(0, options.timeoutMs ?? DEFAULT_SPEECH_WAIT_TIMEOUT_MS);
          let resolveWait: (result: SpeechResult) => void = () => {};
          const promise = new Promise<SpeechResult>((resolve) => {
            resolveWait = resolve;
          });
          const wait: PendingSpeechWait = {
            id,
            text: trimmed,
            started: false,
            streamed: false,
            settled: false,
            timeoutId: null,
            promise,
            resolve: resolveWait,
          };
          wait.timeoutId = window.setTimeout(() => {
            settleSpeechWait(id, { status: "timeout", text: trimmed });
          }, timeoutMs);
          if (options.signal) {
            const abortHandler = () => {
              settleSpeechWait(id, {
                status: "cancelled",
                text: trimmed,
                reason: "caller aborted while waiting for speech",
              });
            };
            options.signal.addEventListener("abort", abortHandler, { once: true });
            wait.removeAbortListener = () => {
              options.signal?.removeEventListener("abort", abortHandler);
            };
          }
          speechWaitsRef.current.set(id, wait);
          queueRef.current.push({ text: trimmed, waitId: id });
          drainQueue();
          return promise;
        },
        clearQueue() {
          ttsEpochRef.current += 1;
          ttsAbortRef.current?.abort();
          ttsAbortRef.current = null;
          queueRef.current = [];
          pcmPushPendingRef.current = new Uint8Array(0);
          settleAllSpeechWaits("cancelled", "Simli speech queue cleared");
          // Also clear Simli's internal audio buffer to stop speech immediately
          clientRef.current?.ClearBuffer();
        },
        pushPcmChunk(chunk: Uint8Array) {
          if (!chunk?.length) return;
          const prev = pcmPushPendingRef.current;
          const merged = new Uint8Array(prev.length + chunk.length);
          merged.set(prev);
          merged.set(chunk, prev.length);
          pcmPushPendingRef.current = merged;

          while (pcmPushPendingRef.current.length >= MIN_PCM_PUSH && clientRef.current) {
            const evenLen = MIN_PCM_PUSH & ~1;
            clientRef.current.sendAudioData(pcmPushPendingRef.current.slice(0, evenLen));
            pcmPushPendingRef.current = pcmPushPendingRef.current.slice(evenLen);
            sendCountRef.current++;
            sendBytesRef.current += evenLen;
            const now = performance.now();
            const gap = lastSendAtRef.current > 0 ? Math.round(now - lastSendAtRef.current) : 0;
            lastSendAtRef.current = now;
            // Log every 10th sendAudioData to avoid flooding
            if (sendCountRef.current % 10 === 0) {
              cpLog(
                "simli-send",
                `#${sendCountRef.current} total=${sendBytesRef.current}b gap=${gap}ms pending=${pcmPushPendingRef.current.length}b`,
              );
            }
          }
        },
        flushPcmBuffer() {
          const pending = pcmPushPendingRef.current;
          if (pending.length > 0 && clientRef.current) {
            const evenLen = pending.length & ~1;
            if (evenLen > 0) {
              clientRef.current.sendAudioData(pending.slice(0, evenLen));
              sendCountRef.current++;
              sendBytesRef.current += evenLen;
              cpLog(
                "simli-flush",
                `#${sendCountRef.current} total=${sendBytesRef.current}b flushed=${evenLen}b`,
              );
            }
          }
          pcmPushPendingRef.current = new Uint8Array(0);
        },
        /** Reset send diagnostics (call on new audio stream start) */
        resetSendStats() {
          sendCountRef.current = 0;
          sendBytesRef.current = 0;
          lastSendAtRef.current = 0;
        },
      }),
      [drainQueue, settleAllSpeechWaits, settleSpeechWait, startFromUserGesture],
    );

    // Drain pending queue when connection becomes ready (covers items enqueued
    // between the 'start' event and the React render cycle that updates isReady)
    useEffect(() => {
      if (isReady) drainQueue();
    }, [isReady, drainQueue]);

    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.muted = isMuted;
        // Unmuting triggers from a click (user gesture) — retry playback if
        // it was previously blocked by autoplay policy.
        if (!isMuted && audioRef.current.paused) {
          void audioRef.current.play().catch(() => {});
        }
      }
    }, [isMuted]);

    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.volume = volume;
      }
    }, [volume]);

    const hasApiKey = !!(import.meta.env.VITE_SIMLI_API_KEY ?? "").trim();

    if (error) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-black/90">
          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-primary/50"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
        </div>
      );
    }

    return (
      <div className="relative w-full h-full">
        <video
          ref={videoRef}
          data-simli-avatar
          autoPlay
          playsInline
          muted={true}
          className="w-full h-full object-cover scale-[1.2]"
          style={{ objectPosition: "center 25%", backgroundColor: "#000", willChange: "transform" }}
        />
        {posterSrc && (
          <img
            src={posterSrc}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            className={cn(
              "pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
              isReady ? "opacity-0" : "opacity-100",
            )}
          />
        )}
        {/* Soft vignette to mask Gaussian splatting edge artifacts */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle, transparent 62%, rgba(0,0,0,0.25) 100%)" }}
        />
        <audio ref={audioRef} autoPlay />
        {hasApiKey && awaitingUserGesture && !hideGestureGate && (
          <button
            type="button"
            onPointerDown={handleUserGestureStart}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-full bg-black/55 px-3 text-center backdrop-blur-[1px] transition-colors hover:bg-black/45"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-white">
              Tap to start avatar
            </span>
            <span className="text-[8px] leading-tight text-white/80">
              Required for audio (browser policy)
            </span>
          </button>
        )}
      </div>
    );
  }),
  (prev, next) =>
    prev.faceId === next.faceId &&
    prev.voiceId === next.voiceId &&
    prev.isMuted === next.isMuted &&
    prev.volume === next.volume &&
    prev.hideGestureGate === next.hideGestureGate &&
    prev.posterSrc === next.posterSrc,
);
